import { useCallback } from 'react';
import { useFlags } from 'launchdarkly-react-client-sdk';

import { tracker } from 'src/utils/analytics';
import type { AccountSignupAction, AccountSignupMethod, AccountSignupSurface } from 'src/utils/analytics/events';

export const ACCOUNT_SIGNUP_ANALYTICS_FLAG = 'fintech.accounts.signup-analytics.rollout';

type TrackFailedArgs = {
  errorCode?: string;
  errorMessage?: string;
};

export type UseAccountSignupAnalyticsOptions = {
  surface: AccountSignupSurface | null;
  method: AccountSignupMethod | null;
  dutchiePaySessionToken?: string;
};

export type UseAccountSignupAnalyticsReturn = {
  trackViewed: () => void;
  trackSubmitted: () => void;
  trackSucceeded: () => void;
  trackFailed: (args?: TrackFailedArgs) => void;
};

export const useAccountSignupAnalytics = ({
  surface,
  method,
  dutchiePaySessionToken,
}: UseAccountSignupAnalyticsOptions): UseAccountSignupAnalyticsReturn => {
  const flags = useFlags();
  const isEnabled = flags[ACCOUNT_SIGNUP_ANALYTICS_FLAG] ?? false;

  const track = useCallback(
    (action: AccountSignupAction, errorFields: TrackFailedArgs = {}): void => {
      if (!isEnabled || surface === null || method === null) {
        return;
      }

      tracker.accountSignupAction({
        action,
        method,
        surface,
        ...(dutchiePaySessionToken !== undefined && { dutchiePaySessionToken }),
        ...errorFields,
      });
    },
    [dutchiePaySessionToken, isEnabled, method, surface]
  );

  const trackViewed = useCallback((): void => track('viewed'), [track]);
  const trackSubmitted = useCallback((): void => track('submitted'), [track]);
  const trackSucceeded = useCallback((): void => track('succeeded'), [track]);
  const trackFailed = useCallback(
    ({ errorCode, errorMessage }: TrackFailedArgs = {}): void => track('failed', { errorCode, errorMessage }),
    [track]
  );

  return { trackViewed, trackSubmitted, trackSucceeded, trackFailed };
};
